import { useEffect, useState, useCallback } from "react";
import { Plus, Loader2, ChevronUp, ChevronDown, Star, Power, PowerOff, Check, X, Pencil, GripVertical, ChevronRight, Trash2, AlertTriangle, History, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading, ErrorView } from "@/components/StateViews";
import ConfirmDialog from "@/components/ConfirmDialog";
import { stageColor, STAGE_COLOR_MAP } from "@/lib/stageColors";
import { useAuth } from "@/contexts/AuthContext";

const LOG_META = {
  create_stage: { label: "Created stage", tone: "text-emerald-600 bg-emerald-50" },
  rename_stage: { label: "Renamed stage", tone: "text-blue-600 bg-blue-50" },
  recolor_stage: { label: "Changed stage colour", tone: "text-blue-600 bg-blue-50" },
  reorder_stages: { label: "Reordered stages", tone: "text-slate-600 bg-slate-100" },
  set_default_stage: { label: "Set default stage", tone: "text-amber-600 bg-amber-50" },
  activate_stage: { label: "Activated stage", tone: "text-emerald-600 bg-emerald-50" },
  deactivate_stage: { label: "Deactivated stage", tone: "text-amber-600 bg-amber-50" },
  delete_stage: { label: "Deleted stage", tone: "text-red-600 bg-red-50" },
  update_stage: { label: "Updated stage", tone: "text-slate-600 bg-slate-100" },
  create_substage: { label: "Created sub-stage", tone: "text-emerald-600 bg-emerald-50" },
  rename_substage: { label: "Renamed sub-stage", tone: "text-blue-600 bg-blue-50" },
  recolor_substage: { label: "Changed sub-stage colour", tone: "text-blue-600 bg-blue-50" },
  activate_substage: { label: "Activated sub-stage", tone: "text-emerald-600 bg-emerald-50" },
  deactivate_substage: { label: "Deactivated sub-stage", tone: "text-amber-600 bg-amber-50" },
  delete_substage: { label: "Deleted sub-stage", tone: "text-red-600 bg-red-50" },
};

function logDetail(e) {
  const d = e.details || {};
  if (e.action === "rename_stage" || e.action === "rename_substage") return `"${d.from}" → "${d.to}"`;
  if (e.action === "delete_stage") {
    const bits = [];
    if (d.migrated_leads) bits.push(`${d.migrated_leads} lead(s) moved to "${d.migrate_to}"`);
    if (d.substages_deleted) bits.push(`${d.substages_deleted} sub-stage(s) removed`);
    return bits.join(" · ") || null;
  }
  if (e.action === "deactivate_stage" && d.migrated_leads) return `${d.migrated_leads} lead(s) moved to "${d.migrate_to}"`;
  if (e.action === "delete_substage" && d.migrated_leads) return `${d.migrated_leads} lead(s) ${d.migrate_to ? `moved to "${d.migrate_to}"` : "untagged"}`;
  if (e.action === "create_stage" && d.is_default) return "Set as default for new leads";
  return null;
}

function PipelineLogs({ open, onOpenChange }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/pipeline/audit", { params: { limit: 200 } })
      .then(({ data }) => setLogs(data.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-sky bg-white sm:max-w-lg" data-testid="pipeline-logs-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><History className="h-5 w-5 text-loop" />Pipeline Setting Logs</DialogTitle>
          <DialogDescription>Every change to your pipeline stages and sub-stages, newest first.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-loop" /></div>
        ) : logs.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink/45" data-testid="pipeline-logs-empty">No pipeline changes recorded yet.</p>
        ) : (
          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1 no-scrollbar" data-testid="pipeline-logs-list">
            {logs.map((e) => {
              const meta = LOG_META[e.action] || { label: e.action, tone: "text-slate-600 bg-slate-100" };
              const detail = logDetail(e);
              return (
                <div key={e.id} data-testid="pipeline-log-row" className="rounded-xl border border-sky/60 bg-mist/30 p-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.tone}`}>{meta.label}</span>
                    {e.entity_name && e.entity_key && <span className="truncate text-sm font-medium text-navy">{e.entity_name}</span>}
                  </div>
                  {detail && <p className="mt-1 flex items-center gap-1 text-[11px] text-ink/55"><ArrowRight className="h-3 w-3 shrink-0" />{detail}</p>}
                  <p className="mt-1 text-[11px] text-ink/40">{e.by_user_name || "System"} · {e.at ? new Date(e.at).toLocaleString() : ""}</p>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Safe deactivate / hard-delete modal with dependency summary + lead migration.
function DangerModal({ open, onOpenChange, entity, mode, id, name, onDone }) {
  const { options } = useAuth();
  const [deps, setDeps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrateTo, setMigrateTo] = useState("");
  const [migrateSubStage, setMigrateSubStage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    setDeps(null); setMigrateTo(""); setMigrateSubStage(""); setLoading(true);
    const url = entity === "stage" ? `/pipeline/stages/${id}/dependencies` : `/pipeline/substages/${id}/dependencies`;
    api.get(url).then(({ data }) => setDeps(data.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open, id, entity]);

  const leadCount = deps?.lead_count || 0;
  const targets = deps?.migration_targets || [];
  // A stage move always needs a destination when it still holds leads. A sub-stage can
  // instead just clear the tag, so migration is optional there.
  const needTarget = entity === "stage" && leadCount > 0;
  const isDelete = mode === "delete";
  // Sub-stages available under the chosen destination stage (optional secondary classification).
  const destSubStages = entity === "stage" && migrateTo ? (options?.substages_by_stage?.[migrateTo] || []) : [];

  const confirm = async () => {
    setBusy(true);
    try {
      let res;
      if (entity === "stage") {
        if (isDelete) res = await api.delete(`/pipeline/stages/${id}`, { params: { ...(migrateTo ? { migrate_to: migrateTo } : {}), ...(migrateSubStage ? { migrate_sub_stage: migrateSubStage } : {}) } });
        else res = await api.post(`/pipeline/stages/${id}/deactivate`, { migrate_to: migrateTo || null, migrate_sub_stage: migrateSubStage || null });
      } else {
        res = await api.delete(`/pipeline/substages/${id}`, { params: migrateTo ? { migrate_to: migrateTo } : {} });
      }
      const moved = res?.data?.migrated || 0;
      toast.success(`${isDelete ? "Deleted" : "Deactivated"} "${name}"${moved ? ` · ${moved} lead(s) moved` : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const labelClr = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink/50";
  const title = `${isDelete ? "Delete" : "Deactivate"} ${entity === "stage" ? "stage" : "sub-stage"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="pipeline-danger-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy">
            <AlertTriangle className={`h-5 w-5 ${isDelete ? "text-red-500" : "text-amber-500"}`} />{title}
          </DialogTitle>
          <DialogDescription>
            {isDelete
              ? `"${name}" will be permanently removed. This can't be undone.`
              : `"${name}" will be hidden from the pipeline, filters and new lead movement. You can reactivate it later.`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-loop" /></div>
        ) : (
          <div className="space-y-3">
            {/* Dependency summary */}
            <div className="space-y-1.5 rounded-xl border border-sky/70 bg-mist/40 p-3 text-xs text-ink/70" data-testid="danger-deps">
              <div className="flex items-center justify-between"><span>Active leads here</span><span className="font-bold text-navy" data-testid="danger-lead-count">{leadCount}</span></div>
              {entity === "stage" && (deps?.substages?.length > 0) && (
                <div className="flex items-center justify-between"><span>Sub-stages {isDelete ? "(will be deleted)" : ""}</span><span className="font-bold text-navy">{deps.substages.length}</span></div>
              )}
              {entity === "stage" && (deps?.qualification_flows?.length > 0) && (
                <div className="flex items-start justify-between gap-2"><span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />Used as a trigger in qualification flow(s)</span><span className="text-right font-medium text-amber-700">{deps.qualification_flows.map((f) => f.name).join(", ")}</span></div>
              )}
              {entity === "stage" && deps?.in_stage_rules && (
                <p className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />This stage has stage/activity rules configured{isDelete ? " (they will be removed)" : ""}.</p>
              )}
            </div>

            {/* Migration picker */}
            {leadCount > 0 && (
              <div data-testid="danger-migrate">
                <label className={labelClr}>{needTarget ? "Move these leads to *" : "Move these leads to"}</label>
                <select value={migrateTo} onChange={(e) => { setMigrateTo(e.target.value); setMigrateSubStage(""); }} data-testid="danger-migrate-select"
                  className="h-10 w-full rounded-xl border border-sky bg-white px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/15">
                  <option value="">{entity === "stage" ? "Select a stage…" : "Clear sub-stage tag"}</option>
                  {targets.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
                {/* Optional sub-stage within the destination stage */}
                {destSubStages.length > 0 && (
                  <div className="mt-2.5" data-testid="danger-migrate-substage">
                    <label className={labelClr}>Move into which sub-stage? (optional)</label>
                    <select value={migrateSubStage} onChange={(e) => setMigrateSubStage(e.target.value)} data-testid="danger-migrate-substage-select"
                      className="h-10 w-full rounded-xl border border-sky bg-white px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/15">
                      <option value="">No sub-stage</option>
                      {destSubStages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                {entity === "stage" && destSubStages.length === 0 && <p className="mt-1 text-[11px] text-ink/45">Their sub-stage tag will be cleared on move.</p>}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={() => onOpenChange(false)} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
              <button onClick={confirm} disabled={busy || (needTarget && !migrateTo)} data-testid="danger-confirm"
                className={`flex flex-[1.4] items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${isDelete ? "bg-red-500 hover:bg-red-600" : "bg-amber-500 hover:bg-amber-600"}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isDelete ? "Delete permanently" : "Deactivate"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ColorSwatches({ value, palette, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {palette.map((c) => (
        <button key={c} type="button" title={c} onClick={() => onChange(c)} data-testid={`color-${c}`}
          className={`h-6 w-6 rounded-full border border-black/10 ${stageColor(c).dot} ring-offset-1 transition-all ${value === c ? "ring-2 ring-navy" : "hover:scale-110"}`} />
      ))}
    </div>
  );
}

function SubStageManager({ parentKey, parentName, palette }) {
  const { refreshOptions } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newSub, setNewSub] = useState({ name: "", color: "slate" });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [danger, setDanger] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pipeline/substages", { params: { parent_stage_key: parentKey } });
      setSubs(data.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [parentKey]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try { await api.patch(`/pipeline/substages/${id}`, body); await load(); await refreshOptions(); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const add = async () => {
    if (!newSub.name.trim()) return toast.error("Sub-stage name is required");
    setSaving(true);
    try {
      await api.post("/pipeline/substages", { parent_stage_key: parentKey, ...newSub, name: newSub.name.trim() });
      toast.success("Sub-stage created");
      setNewSub({ name: "", color: "slate" }); setAdding(false); await load(); await refreshOptions();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= subs.length) return;
    const r = [...subs];
    [r[idx], r[j]] = [r[j], r[idx]];
    setSubs(r);
    try { await api.post("/pipeline/substages/reorder", { order: r.map((s) => s.id) }); await refreshOptions(); }
    catch (e) { toast.error(apiError(e)); load(); }
  };
  const startEdit = (s) => { setEditId(s.id); setEditName(s.name); };
  const commitEdit = async (s) => {
    const nn = editName.trim();
    setEditId(null);
    if (!nn || nn === s.name) return;
    const ok = await patch(s.id, { name: nn });
    if (ok) toast.success("Sub-stage renamed");
  };

  return (
    <div className="rounded-xl border border-dashed border-sky bg-mist/40 p-3" data-testid={`substage-manager-${parentKey}`}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Sub-stages of {parentName}</p>
        {!adding && <button onClick={() => setAdding(true)} data-testid={`add-substage-${parentKey}`} className="flex items-center gap-1 rounded-lg border border-loop bg-white px-2 py-1 text-[11px] font-semibold text-loop hover:bg-loop hover:text-white"><Plus className="h-3 w-3" />Add</button>}
      </div>

      {adding && (
        <div className="mb-3 space-y-2 rounded-lg border border-sky bg-white p-2.5" data-testid={`new-substage-form-${parentKey}`}>
          <input autoFocus value={newSub.name} onChange={(e) => setNewSub((s) => ({ ...s, name: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="e.g. Call Not Picked" data-testid={`new-substage-name-${parentKey}`}
            className="h-9 w-full rounded-lg border border-sky px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/15" />
          <ColorSwatches value={newSub.color} palette={palette} onChange={(c) => setNewSub((s) => ({ ...s, color: c }))} testid={`new-substage-colors-${parentKey}`} />
          <div className="flex gap-2">
            <button onClick={add} disabled={saving} data-testid={`save-substage-${parentKey}`} className="flex items-center gap-1 rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover disabled:opacity-60">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}Add</button>
            <button onClick={() => { setAdding(false); setNewSub({ name: "", color: "slate" }); }} className="rounded-lg border border-sky px-3 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="py-3 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-loop" /></div>
        : subs.length === 0 && !adding ? <p className="py-1 text-xs text-ink/40">No sub-stages yet. Add one to classify leads within {parentName}.</p>
        : (
          <div className="space-y-1.5" data-testid={`substage-list-${parentKey}`}>
            {subs.map((s, idx) => {
              const col = stageColor(s.color);
              return (
                <div key={s.id} data-testid={`substage-row-${s.key}`} className={`flex items-center gap-2 rounded-lg border bg-white p-2 ${s.is_active ? "border-sky/70" : "border-dashed border-slate-300 opacity-70"}`}>
                  <span className={`h-3.5 w-3.5 shrink-0 rounded-full border border-black/10 ${col.dot}`} />
                  <div className="min-w-0 flex-1">
                    {editId === s.id ? (
                      <div className="flex items-center gap-1">
                        <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitEdit(s); if (e.key === "Escape") setEditId(null); }} data-testid={`substage-rename-input-${s.key}`}
                          className="h-7 w-full max-w-[180px] rounded-md border border-loop px-2 text-xs outline-none" />
                        <button onClick={() => commitEdit(s)} className="text-emerald-600"><Check className="h-3.5 w-3.5" /></button>
                        <button onClick={() => setEditId(null)} className="text-ink/40"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-navy">{s.name}</span>
                        {!s.is_active && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-500">Inactive</span>}
                      </div>
                    )}
                  </div>
                  <details className="relative shrink-0">
                    <summary className="flex cursor-pointer list-none items-center gap-1 rounded-md border border-sky px-1.5 py-0.5 text-[10px] text-ink/55 hover:bg-mist"><span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />Colour</summary>
                    <div className="absolute right-0 z-10 mt-1 w-52 rounded-xl border border-sky bg-white p-2.5 shadow-xl">
                      <ColorSwatches value={s.color} palette={palette} onChange={(c) => patch(s.id, { color: c })} testid={`substage-colors-${s.key}`} />
                    </div>
                  </details>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`substage-up-${s.key}`} className="rounded p-1 text-ink/40 hover:bg-sky/40 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === subs.length - 1} data-testid={`substage-down-${s.key}`} className="rounded p-1 text-ink/40 hover:bg-sky/40 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                    <button onClick={() => startEdit(s)} data-testid={`substage-rename-${s.key}`} title="Rename" className="rounded p-1 text-ink/40 hover:text-loop"><Pencil className="h-3 w-3" /></button>
                    {s.is_active ? (
                      <button onClick={() => patch(s.id, { is_active: false })} data-testid={`substage-deactivate-${s.key}`} title="Deactivate" className="rounded p-1 text-ink/40 hover:text-red-500"><PowerOff className="h-3 w-3" /></button>
                    ) : (
                      <button onClick={() => patch(s.id, { is_active: true })} data-testid={`substage-activate-${s.key}`} title="Activate" className="rounded p-1 text-emerald-500 hover:bg-emerald-50"><Power className="h-3 w-3" /></button>
                    )}
                    {!s.is_active && !s.is_system && (
                      <button onClick={() => setDanger({ entity: "substage", mode: "delete", id: s.id, name: s.name })} data-testid={`substage-delete-${s.key}`} title="Delete permanently" className="rounded p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-3 w-3" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <DangerModal
        open={!!danger}
        onOpenChange={(v) => !v && setDanger(null)}
        entity={danger?.entity}
        mode={danger?.mode}
        id={danger?.id}
        name={danger?.name}
        onDone={() => { setDanger(null); load(); refreshOptions(); }}
      />
    </div>
  );
}

export default function PipelineSettings() {
  const { refreshOptions } = useAuth();
  const [stages, setStages] = useState([]);
  const [palette, setPalette] = useState(Object.keys(STAGE_COLOR_MAP));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newStage, setNewStage] = useState({ name: "", color: "blue", is_default: false });
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [pendingRename, setPendingRename] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [justAddedKey, setJustAddedKey] = useState(null);
  const [danger, setDanger] = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/pipeline/stages");
      setStages(data.data);
      if (data.palette?.length) setPalette(data.palette);
      await refreshOptions();
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [refreshOptions]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try { await api.patch(`/pipeline/stages/${id}`, body); await load(); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };

  const addStage = async () => {
    if (!newStage.name.trim()) return toast.error("Stage name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/pipeline/stages", newStage);
      toast.success("Stage created");
      setJustAddedKey(data?.data?.key || null);
      setNewStage({ name: "", color: "blue", is_default: false }); setAdding(false); await load();
      setTimeout(() => setJustAddedKey(null), 1200);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= stages.length) return;
    const reordered = [...stages];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    setStages(reordered); // optimistic
    try { await api.post("/pipeline/stages/reorder", { order: reordered.map((s) => s.id) }); await refreshOptions(); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const setDefault = async (id) => {
    try { await api.post(`/pipeline/stages/${id}/default`); toast.success("Default stage updated"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const startEdit = (s) => { setEditId(s.id); setEditName(s.name); };
  const commitEdit = (s) => {
    const nn = editName.trim();
    setEditId(null);
    if (!nn || nn === s.name) return;
    setPendingRename({ id: s.id, name: nn, old: s.name });
  };
  const confirmRename = async () => {
    if (!pendingRename) return;
    const ok = await patch(pendingRename.id, { name: pendingRename.name });
    if (ok) toast.success("Stage renamed");
    setPendingRename(null);
  };

  if (loading) return <Loading label="Loading pipeline stages..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const activeCount = stages.filter((s) => s.is_active).length;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="pipeline-settings-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-sora text-3xl font-bold text-navy">Pipeline Settings</h1>
          <p className="text-sm text-ink/60">Configure the stages that power your Kanban board, filters, qualification, rules and lead movement. Renaming a stage only changes its label — existing leads and rules stay attached.</p>
        </div>
        <button onClick={() => setShowLogs(true)} data-testid="open-pipeline-logs" className="flex shrink-0 items-center gap-1.5 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-semibold text-ink/70 hover:border-loop hover:text-loop"><History className="h-4 w-4" />Setting Logs</button>
      </div>

      {/* Add stage */}
      <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid="add-stage-card">
        {!adding ? (
          <button onClick={() => setAdding(true)} data-testid="add-stage-button" className="flex items-center gap-1.5 rounded-lg bg-loop px-3 py-2 text-sm font-semibold text-white hover:bg-loophover"><Plus className="h-4 w-4" />New stage</button>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1">
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-ink/40">Stage name</label>
                <input autoFocus value={newStage.name} onChange={(e) => setNewStage((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Demo Booked" data-testid="new-stage-name"
                  className="h-10 w-full rounded-xl border border-sky px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/15" />
              </div>
              <label className="flex items-center gap-2 pb-2.5 text-sm text-ink/70"><input type="checkbox" checked={newStage.is_default} onChange={(e) => setNewStage((s) => ({ ...s, is_default: e.target.checked }))} data-testid="new-stage-default" />Default for new leads</label>
            </div>
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-ink/40">Highlight colour</label>
              <ColorSwatches value={newStage.color} palette={palette} onChange={(c) => setNewStage((s) => ({ ...s, color: c }))} testid="new-stage-colors" />
            </div>
            <div className="flex gap-2">
              <button onClick={addStage} disabled={saving} data-testid="save-new-stage" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Add stage</button>
              <button onClick={() => { setAdding(false); setNewStage({ name: "", color: "blue", is_default: false }); }} className="rounded-lg border border-sky px-4 py-2 text-sm font-medium text-ink/60 hover:bg-mist">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Stage list */}
      <div className="space-y-2" data-testid="stage-list">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/40">{activeCount} active stages · ordered top → bottom = left → right on Kanban</p>
        {stages.map((s, idx) => {
          const col = stageColor(s.color);
          return (
            <div key={s.id} style={s.key === justAddedKey ? { animation: "stageIn 0.45s ease-out" } : undefined}>
            <div data-testid={`stage-row-${idx}`} className={`flex items-center gap-3 rounded-xl border bg-white p-3 ${s.is_active ? "border-sky/70" : "border-dashed border-slate-300 opacity-70"} ${s.key === justAddedKey ? "ring-2 ring-loop/40" : ""}`}>
              <GripVertical className="h-4 w-4 shrink-0 text-ink/20" />
              <span className={`h-5 w-5 shrink-0 rounded-full border border-black/10 ${col.dot}`} />
              <div className="min-w-0 flex-1">
                {editId === s.id ? (
                  <div className="flex items-center gap-1.5">
                    <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commitEdit(s); if (e.key === "Escape") setEditId(null); }} data-testid={`rename-input-${idx}`}
                      className="h-8 w-full max-w-[220px] rounded-lg border border-loop px-2 text-sm outline-none" />
                    <button onClick={() => commitEdit(s)} className="text-emerald-600"><Check className="h-4 w-4" /></button>
                    <button onClick={() => setEditId(null)} className="text-ink/40"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-sora text-sm font-semibold text-navy">{s.name}</span>
                    {s.is_default && <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />Default</span>}
                    {!s.is_active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Inactive</span>}
                    {s.is_system && <span className="rounded-full bg-sky/40 px-2 py-0.5 text-[10px] font-medium text-loop">System</span>}
                  </div>
                )}
              </div>
              {/* color picker */}
              <details className="relative shrink-0">
                <summary className="flex cursor-pointer list-none items-center gap-1 rounded-lg border border-sky px-2 py-1 text-[11px] text-ink/60 hover:bg-mist"><span className={`h-3 w-3 rounded-full ${col.dot}`} />Colour</summary>
                <div className="absolute right-0 z-10 mt-1 w-56 rounded-xl border border-sky bg-white p-2.5 shadow-xl">
                  <ColorSwatches value={s.color} palette={palette} onChange={(c) => patch(s.id, { color: c })} testid={`colors-${idx}`} />
                </div>
              </details>
              <div className="flex shrink-0 items-center gap-0.5">
                <button onClick={() => setExpandedId(expandedId === s.id ? null : s.id)} data-testid={`toggle-substages-${idx}`} title="Manage sub-stages" className="rounded p-1 text-ink/40 hover:text-loop"><ChevronRight className={`h-4 w-4 transition-transform ${expandedId === s.id ? "rotate-90 text-loop" : ""}`} /></button>
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`move-up-${idx}`} className="rounded p-1 text-ink/40 hover:bg-sky/40 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === stages.length - 1} data-testid={`move-down-${idx}`} className="rounded p-1 text-ink/40 hover:bg-sky/40 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => startEdit(s)} data-testid={`rename-${idx}`} title="Rename" className="rounded p-1 text-ink/40 hover:text-loop"><Pencil className="h-3.5 w-3.5" /></button>
                {!s.is_default && <button onClick={() => setDefault(s.id)} disabled={!s.is_active} data-testid={`set-default-${idx}`} title="Set as default for new leads" className="rounded p-1 text-ink/40 hover:text-amber-500 disabled:opacity-30"><Star className="h-3.5 w-3.5" /></button>}
                {s.is_active ? (
                  <button onClick={() => setDanger({ entity: "stage", mode: "deactivate", id: s.id, name: s.name })} data-testid={`deactivate-${idx}`} title="Deactivate" className="rounded p-1 text-ink/40 hover:text-red-500"><PowerOff className="h-3.5 w-3.5" /></button>
                ) : (
                  <button onClick={() => patch(s.id, { is_active: true })} data-testid={`activate-${idx}`} title="Activate" className="rounded p-1 text-emerald-500 hover:bg-emerald-50"><Power className="h-3.5 w-3.5" /></button>
                )}
                {!s.is_active && !s.is_system && (
                  <button onClick={() => setDanger({ entity: "stage", mode: "delete", id: s.id, name: s.name })} data-testid={`delete-stage-${idx}`} title="Delete permanently" className="rounded p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>
            {expandedId === s.id && (
              <div className="ml-7 mt-1.5" data-testid={`substages-panel-${idx}`}>
                <SubStageManager parentKey={s.key} parentName={s.name} palette={palette} />
              </div>
            )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!pendingRename}
        onOpenChange={(v) => !v && setPendingRename(null)}
        onConfirm={confirmRename}
        title="Rename this stage?"
        description={pendingRename ? `"${pendingRename.old}" will be renamed to "${pendingRename.name}" everywhere. Existing leads and rules stay attached to this same stage — nothing is moved.` : ""}
        confirmLabel="Rename stage"
        testid="rename-stage-dialog"
      />

      <DangerModal
        open={!!danger}
        onOpenChange={(v) => !v && setDanger(null)}
        entity={danger?.entity}
        mode={danger?.mode}
        id={danger?.id}
        name={danger?.name}
        onDone={() => { setDanger(null); load(); }}
      />

      <PipelineLogs open={showLogs} onOpenChange={setShowLogs} />
    </div>
  );
}
